import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Mail, Send, Eye, Check, X, Calendar, Users, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const PendingGuestEmails = ({ user }) => {
  const navigate = useNavigate();
  const [pendingEmails, setPendingEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewEmail, setPreviewEmail] = useState(null);

  useEffect(() => {
    loadPendingEmails();
  }, [user.id]);

  const loadPendingEmails = async () => {
    try {
      const response = await axios.get(`${API}/guest-emails/pending/${user.id}`);
      setPendingEmails(response.data);
    } catch (error) {
      console.error('Error loading pending emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (emailId) => {
    try {
      await axios.post(`${API}/guest-emails/${emailId}/approve`);
      toast.success('Email approved and sent! 📧');
      loadPendingEmails();
    } catch (error) {
      toast.error('Failed to send email');
    }
  };

  const handleReject = async (emailId) => {
    try {
      await axios.post(`${API}/guest-emails/${emailId}/reject`);
      toast.success('Email cancelled');
      loadPendingEmails();
    } catch (error) {
      toast.error('Failed to cancel email');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4]">
      {/* Navigation */}
      <nav className="bg-white/70 backdrop-blur-xl border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-stone-100 rounded-full transition-colors">
                <ArrowLeft size={20} className="text-[#57534E]" />
              </button>
              <Heart className="text-[#881337]" size={24} fill="#881337" />
              <span className="text-xl font-serif font-bold text-[#1C1917]">VowFlow</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-[#1C1917] mb-2">Pending Guest Emails</h1>
          <p className="text-[#57534E]">Review and approve automated emails before they're sent to your guests</p>
        </div>

        {pendingEmails.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-stone-200">
            <Mail className="mx-auto text-stone-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-[#1C1917] mb-2">No Pending Emails</h3>
            <p className="text-[#57534E] text-sm">
              Your automated guest emails will appear here for approval before sending.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingEmails.map((email) => (
              <div key={email.id} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="text-[#881337]" size={20} />
                      <h3 className="text-lg font-medium text-[#1C1917]">{email.subject}</h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#57534E]">
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {email.recipient_count} recipients
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Scheduled for: {new Date(email.scheduled_for).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {email.trigger_type}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setPreviewEmail(email)}
                      className="bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-2"
                    >
                      <Eye size={16} />
                      Preview
                    </Button>
                    <Button
                      onClick={() => handleApprove(email.id)}
                      className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                    >
                      <Check size={16} />
                      Approve & Send
                    </Button>
                    <Button
                      onClick={() => handleReject(email.id)}
                      className="bg-red-100 hover:bg-red-200 text-red-700 flex items-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </Button>
                  </div>
                </div>

                {email.preview_text && (
                  <div className="bg-stone-50 rounded-lg p-4 text-sm text-stone-700">
                    <p className="font-medium mb-1">Preview:</p>
                    <p className="line-clamp-2">{email.preview_text}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewEmail && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <h2 className="text-xl font-serif font-bold">Email Preview</h2>
              <button onClick={() => setPreviewEmail(null)} className="text-stone-400 hover:text-stone-600">
                <X size={24} />
              </button>
            </div>
            
            <div className="overflow-y-auto p-6 max-h-[70vh]">
              <div className="mb-4">
                <p className="text-sm text-stone-500 mb-1">Subject:</p>
                <p className="font-medium text-[#1C1917]">{previewEmail.subject}</p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-stone-500 mb-1">Recipients: {previewEmail.recipient_count} guests</p>
              </div>
              
              <div className="border border-stone-200 rounded-lg p-6 bg-stone-50">
                <div dangerouslySetInnerHTML={{ __html: previewEmail.html_preview }} />
              </div>
            </div>
            
            <div className="flex gap-3 p-6 border-t border-stone-200">
              <Button
                onClick={() => setPreviewEmail(null)}
                className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  handleApprove(previewEmail.id);
                  setPreviewEmail(null);
                }}
                className="flex-1 bg-[#881337] hover:bg-[#9F1239] text-white"
              >
                Approve & Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingGuestEmails;
